'use client';

import classNames from 'classnames';
import { useCallback, useContext, useState } from 'react';

import { Trans } from 'react-i18next';

import { CookiebotContext } from '@/app/components/CookiebotScript/CookiebotScript';

import PlayIcon from '@/app/components/PlayIcon/PlayIcon';

export enum MediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface VideoDisplayProps {
  type: MediaType.VIDEO;
  videoUrl: string;
  imageUrl: string;
  sizes: string;
  imageAlt?: string;
  onClick?: () => void;
  id?: string;
}

export interface ImageDisplayProps {
  type: MediaType.IMAGE;
  largeImageSrc: string;
  smallImageSrc: string;
  sizes: string;
  alt?: string;
  isLandscapeOriented: boolean;
  onClick?: () => void;
  id?: string;
}

export type MediaDisplayProps = ImageDisplayProps | VideoDisplayProps;

const MediaDisplay = (props: MediaDisplayProps) => {
  const [isVideoCoverImageVisible, setIsVideoCoverImageVisible] = useState(true);
  const { areMarketingCookiesAllowed } = useContext(CookiebotContext);

  const handleVideoCoverImageClick = useCallback(() => {
    setIsVideoCoverImageVisible(false);

    props.onClick?.();
  }, [setIsVideoCoverImageVisible, props]);

  const handleCookiesConsentClick = () => {
    if (window?.CookieConsent?.renew) {
      window.CookieConsent.renew();
    } else {
      console.warn('CookieConsent.renew() is not available yet.');
    }
  };
  if (props.type === MediaType.IMAGE) {
    return (
      <img
        src={props.largeImageSrc}
        alt={props.alt ?? ' - '}
        className={classNames(
          'absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] max-w-full max-h-full',
          {
            'cursor-pointer': !!props.onClick,
            'object-contain': !props.isLandscapeOriented,
            'object-cover': props.isLandscapeOriented,
          },
        )}
        fetchPriority="high"
        sizes={props.sizes}
        onClick={props.onClick}
      />
    );
  }

  if (props.type === MediaType.VIDEO) {
    return (
      <>
        {!areMarketingCookiesAllowed && (
          <div className="absolute w-full h-full justify-center flex items-center">
            <span className="block w-[80%]">
              <Trans i18nKey="marketingCookiesConsent">
                Prosím{' '}
                <button
                  className="inline underline text-primary-mid hover:text-primary-dark transition-colors hover:no-underline"
                  onClick={handleCookiesConsentClick}
                >
                  povolte marketingové cookies
                </button>
                , abychom vám mohli video zobrazit
              </Trans>
            </span>
          </div>
        )}
        {!isVideoCoverImageVisible && areMarketingCookiesAllowed && (
          <iframe
            width={'100%'}
            height={'100%'}
            src={props.videoUrl}
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            title={props.imageAlt ?? ' - '}
            data-gtm-yt-inspected-10="true"
            className="absolute top-0 left-0 max-w-full max-h-full object-contain"
          />
        )}
        {isVideoCoverImageVisible && areMarketingCookiesAllowed && (
          <>
            <img
              src={props.imageUrl}
              alt={props.imageAlt ?? ' - '}
              className={classNames(
                'absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] w-full object-cover cursor-pointer',
              )}
              fetchPriority="high"
              sizes={props.sizes}
              onClick={handleVideoCoverImageClick}
            />
            <PlayIcon
              onClick={handleVideoCoverImageClick}
              className="text-primary-mid hover:text-primary-dark transition-colors absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%] cursor-pointer"
            />
          </>
        )}
      </>
    );
  }
};

export default MediaDisplay;
